import { useState } from 'react';
import { api, type MealsByDate } from '../lib/api';
import { Card } from './Card';
import { LoadingState } from './LoadingState';
import { ShareMealsButton } from './MealBlock';
import { formatMealPlanText } from '../lib/mealText';

// tomorrowISO returns the YYYY-MM-DD after `todayISO`. Parsed/added in UTC so it
// can't drift by a day in UTC+ timezones the way a local-midnight Date would.
function tomorrowISO(todayISO: string): string {
  const ms = Date.parse(todayISO.slice(0, 10) + 'T00:00:00Z');
  if (Number.isNaN(ms)) return todayISO;
  return new Date(ms + 86_400_000).toISOString().slice(0, 10);
}

// TomorrowPrep — a collapsible peek at tomorrow's planned meals so you can shop
// or cook the night before. Read-only; fetches lazily the first time it opens.
// Flags a block transition (new macro targets) when tomorrow crosses A→B / B→C.
export function TomorrowPrep({
  todayISO,
  todayBlock,
  eatingWindow,
}: {
  todayISO: string;
  todayBlock?: string;
  // Passed down from Today, which is where the dashboard payload (and with it
  // the user's window) is already loaded. Absent = leave it out of the share
  // text rather than guess a window.
  eatingWindow?: { start: string; end: string } | null;
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<MealsByDate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const tISO = tomorrowISO(todayISO);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !data && !error) {
      api
        .getMealsByDate(tISO)
        .then(setData)
        .catch((e) => setError(String(e)));
    }
  };

  const meals = data?.meals.meals ?? [];
  const blockChanges = Boolean(data && todayBlock && data.block !== todayBlock);

  const shareText = () =>
    formatMealPlanText({
      dateISO: tISO,
      block: data?.block,
      meals,
      dailyTotals: data?.meals.daily_totals,
      eatingWindow,
    });

  return (
    <Card
      eyebrow="Prep ahead"
      interactive={!open}
      // Only offered once tomorrow's plan is actually on screen — there is
      // nothing to share before the lazy fetch lands.
      action={open && meals.length > 0 ? <ShareMealsButton text={shareText} /> : undefined}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <span className="text-[15px] font-bold tracking-[-0.01em] text-fg">
          Tomorrow’s meals
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint shrink-0">
          {open ? '▾' : '▸'} {tISO.slice(5)}
        </span>
      </button>

      {open && (
        <div className="space-y-3 pt-1">
          {error && <p className="text-xs text-danger">{error}</p>}
          {!data && !error && (
            <div className="py-2">
              <LoadingState />
            </div>
          )}
          {blockChanges && data && (
            <p className="text-xs text-warn leading-relaxed">
              Heads up — tomorrow moves into Block {data.block}. New calorie and
              macro targets apply.
            </p>
          )}
          {data?.meals.daily_totals && (
            <div className="flex items-baseline justify-between gap-3 pb-1 border-b border-line">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
                Day target
              </span>
              <span className="font-mono text-[11px] tabular-nums text-fg-muted">
                {data.meals.daily_totals.calories} kcal · {data.meals.daily_totals.protein_g}g protein
              </span>
            </div>
          )}
          {meals.map((m) => (
            <div
              key={m.id}
              className="rounded-panel border border-line p-3 space-y-2"
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-bold tracking-[-0.01em] text-fg">{m.label}</span>
                <span className="font-mono text-[11px] tabular-nums text-fg-faint shrink-0">
                  {m.calories} kcal
                  {m.protein_g != null ? ` · ${m.protein_g}p` : ''}
                </span>
              </div>
              <ul className="space-y-1">
                {m.ingredients.map((ing, i) => (
                  <li key={`${i}-${ing.item}`} className="text-sm text-fg flex gap-2">
                    <span className="text-fg-faint shrink-0">·</span>
                    <span>
                      <span className="font-mono tabular-nums text-fg-muted">{ing.amount}</span>{' '}
                      {ing.item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {data && meals.length === 0 && (
            <p className="text-xs text-fg-muted leading-relaxed">
              No fixed recipe for tomorrow’s block — eat to the macro target and
              log it.
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
